import {
  useQuery,
  UseQueryResult,
  useMutation,
  UseMutationResult,
  useQueryClient,
  QueryKey,
} from 'react-query';
import { FieldSectionApi, CreateFieldSectionPayload } from 'api/field-sections';
import { FieldSection, FieldSectionType } from 'api/field-sections/types';
import { ALL_FIELDS_QUERY_KEY } from 'queries/constants';

/* PROJECT_FIELD_SECTIONS_QUERY_KEY_NEW will be renamed to PROJECT_FIELD_SECTIONS_QUERY_KEY after moving project-field-sections implementation to field-sections common implementation */
export const PROJECT_FIELD_SECTIONS_QUERY_KEY_NEW = 'project-field-sections';
export const TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY = 'user-field-sections';
export const TASK_FIELD_SECTIONS_QUERY_KEY = 'task-field-sections';

const queryKeyMap = new Map<FieldSectionType, QueryKey>([
  [FieldSectionType.Project, PROJECT_FIELD_SECTIONS_QUERY_KEY_NEW],
  [FieldSectionType.Task, TASK_FIELD_SECTIONS_QUERY_KEY],
  [FieldSectionType.Users, TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY],
]);

export const getFieldSectionQueryKey = (
  fieldSectionType: FieldSectionType,
): QueryKey => {
  return queryKeyMap.get(fieldSectionType)!;
};

export const useFieldSectionsQuery = (
  fieldSectionType: FieldSectionType,
  enabled = true,
): UseQueryResult<FieldSection[]> => {
  const QUERY_KEY = getFieldSectionQueryKey(fieldSectionType);

  return useQuery({
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    queryKey: QUERY_KEY,
    queryFn: () => {
      return FieldSectionApi.getAllFieldSections(fieldSectionType);
    },
  });
};

export const deleteFieldSectionV2 = (
  fieldSection: FieldSection[],
  deleteFieldSectionId: number,
): FieldSection[] => {
  const updatedFieldSections: FieldSection[] = [];
  const deletedFieldSection = fieldSection.find(
    (section) => section.fieldSectionId === deleteFieldSectionId,
  );

  const deletedFieldSectionOrder =
    deletedFieldSection && deletedFieldSection.fieldOrder
      ? deletedFieldSection.fieldOrder
      : [];

  fieldSection.forEach((section) => {
    if (section !== deletedFieldSection) {
      updatedFieldSections.push({
        ...section,
        fieldOrder: section.default
          ? [...section.fieldOrder, ...deletedFieldSectionOrder] // Append the deleted section field order to default section
          : section.fieldOrder,
      });
    }
  });

  return updatedFieldSections;
};

export const useDeleteFieldSectionMutationV2 = (
  fieldSectionType: FieldSectionType,
): UseMutationResult<void, unknown, number> => {
  const queryClient = useQueryClient();
  const QUERY_KEY = getFieldSectionQueryKey(fieldSectionType);

  return useMutation<
    void,
    unknown,
    number,
    { previous: FieldSection[] | undefined }
  >((fieldSectionId) => FieldSectionApi.deleteFieldSection(fieldSectionId), {
    onMutate: async (fieldSectionId) => {
      await queryClient.cancelQueries(QUERY_KEY);
      const previousFieldSections =
        queryClient.getQueryData<FieldSection[]>(QUERY_KEY);

      if (previousFieldSections) {
        const updatedFieldSections = deleteFieldSectionV2(
          previousFieldSections,
          fieldSectionId,
        );

        queryClient.setQueryData<FieldSection[]>(
          QUERY_KEY,
          updatedFieldSections,
        );
      }

      return { previous: previousFieldSections };
    },
    onError: (err, fieldSections, context) => {
      queryClient.setQueryData(QUERY_KEY, context?.previous);
    },
  });
};

interface UpdateFieldSectionOrderProps {
  updatedFieldSections: FieldSection[];
  fieldId: number;
  fieldSectionId: number;
  fieldOrderIndex: number;
}

export const useUpdateFieldSectionOrderMutationV2 = (
  fieldSectionType: FieldSectionType,
): UseMutationResult<FieldSection[], unknown, UpdateFieldSectionOrderProps> => {
  const queryClient = useQueryClient();
  const QUERY_KEY = getFieldSectionQueryKey(fieldSectionType);

  return useMutation<
    FieldSection[],
    unknown,
    UpdateFieldSectionOrderProps,
    { previous: FieldSection[] | undefined }
  >(
    ({ fieldId, fieldSectionId, fieldOrderIndex }) =>
      FieldSectionApi.updateFieldSectionOrder({
        fieldId,
        fieldSectionId,
        fieldOrderIndex,
      }),
    {
      onMutate: async ({ updatedFieldSections }) => {
        const previous = queryClient.getQueryData<FieldSection[]>(QUERY_KEY);

        queryClient.setQueryData(QUERY_KEY, updatedFieldSections);

        return { previous };
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(ALL_FIELDS_QUERY_KEY);
      },
      onError: (err, fieldSections, context) => {
        if (QUERY_KEY) {
          queryClient.setQueryData(QUERY_KEY, context?.previous);
        }
      },
    },
  );
};

export const useCreatingFieldSectionMutationV2 = (
  fieldSectionType: FieldSectionType,
): UseMutationResult<FieldSection, unknown, CreateFieldSectionPayload> => {
  const queryClient = useQueryClient();
  const QUERY_KEY = getFieldSectionQueryKey(fieldSectionType);

  return useMutation<FieldSection, unknown, CreateFieldSectionPayload>(
    (createFieldSectionPayload) =>
      FieldSectionApi.createFieldSection(createFieldSectionPayload),
    {
      onSuccess: (data) => {
        if (QUERY_KEY) {
          const previous = queryClient.getQueryData<FieldSection[]>(QUERY_KEY);

          if (previous) {
            queryClient.setQueryData(QUERY_KEY, [...previous, data]);
          }
        }
      },
    },
  );
};

type UpdateFieldSectionPayload = Pick<
  FieldSection,
  | 'fieldSectionId'
  | 'fieldSectionName'
  | 'fieldSectionType'
  | 'private'
  | 'readUserGroupIds'
  | 'writeUserGroupIds'
>;

export const updateFieldSectionV2 = (
  fieldSections: FieldSection[],
  fieldSectionId: number,
  fieldSectionName: string,
  isPrivate: boolean,
): FieldSection[] => {
  return fieldSections.map((section) =>
    section.fieldSectionId === fieldSectionId
      ? { ...section, fieldSectionName, private: isPrivate }
      : section,
  );
};

export const useUpdateFieldSectionMutationV2 = (
  fieldSectionType: FieldSectionType,
): UseMutationResult<FieldSection, unknown, UpdateFieldSectionPayload> => {
  const queryClient = useQueryClient();
  const QUERY_KEY = getFieldSectionQueryKey(fieldSectionType);

  return useMutation<FieldSection, unknown, UpdateFieldSectionPayload>(
    (fieldSectionPayload) =>
      FieldSectionApi.updateFieldSection(fieldSectionPayload),
    {
      onSuccess: (
        data,
        { fieldSectionId, fieldSectionName, private: isPrivate },
      ) => {
        if (QUERY_KEY) {
          const previous = queryClient.getQueryData<FieldSection[]>(QUERY_KEY);

          if (previous) {
            const updatedSections = updateFieldSectionV2(
              previous,
              fieldSectionId,
              fieldSectionName,
              isPrivate ?? false,
            );

            queryClient.setQueryData(QUERY_KEY, updatedSections);
          }
        }
      },
    },
  );
};
